import { useState } from "react";
import { Link } from "react-router-dom";
import { NavLink } from "@/components/NavLink";
import {
  ChevronRight,
  Bell,
  Plus,
  User,
  Settings,
  HelpCircle,
  FileText,
  LogOut,
  PieChart,
  LineChart,
  Shield,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { TruncatedText } from "@/components/ui/truncated-text";
import { AddBrandModal } from "@/components/shared/AddBrandModal";
import { NotificationsModal } from "../NotificationsModal";
import { HelpSupportModal } from "../HelpSupportModal";
import { DocumentationModal } from "../DocumentationModal";
import { RoutePath } from "@/routes";
import { accountApi } from "@/resources/account";
import { notificationApi } from "@/resources/notification";
import { ACCOUNT_ROLE } from "@/constants";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { EXPANDED_ITEM, COLLAPSED_ITEM, ICON_CLASS_SM } from "./constants";
import { CollapsibleNavTooltip } from "./CollapsibleNavTooltip";

interface SidebarFooterProps {
  collapsed: boolean;
}

export function SidebarFooter({ collapsed }: SidebarFooterProps) {
  const [addBrandModalOpen, setAddBrandModalOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [docsOpen, setDocsOpen] = useState(false);

  const { data: account } = accountApi.useGetAccount();
  const { data: unreadData } = notificationApi.useGetUnreadCount();
  const unreadCount = unreadData?.count ?? 0;
  const { mutate: signOut } = accountApi.useSignOut();

  const canManageWorkspace =
    account?.role === ACCOUNT_ROLE.OWNER ||
    account?.role === ACCOUNT_ROLE.ADMIN;
  const canMutateBrands = canManageWorkspace;
  const readOnly = useViewerReadOnly();


  const itemClass = collapsed ? COLLAPSED_ITEM : EXPANDED_ITEM;

  return (
    <>
      {/*
        AUT-364: `overflow-x-hidden` mirrors what `SidebarContent` already does for the nav above
        (`components/ui/sidebar.tsx`, the `data-sidebar="content"` div). This footer is a **sibling**
        of that element, not a child, so nothing was clipping it: at the intermediate widths of the
        collapse/expand animation its labels were painted 20-59px outside the panel, over the page.
        `whitespace-nowrap` on each label below stops the two-line wrap that resized the rows; this
        stops the one line it leaves behind from escaping. The user dropdown is portalled by Radix,
        so it is unaffected by the clip.
      */}
      <div className="mt-auto border-t border-black/15 pb-4 overflow-x-hidden">
        <div className="px-2 pt-2">
          {/* Portfolio Analytics (disabled) */}
          <CollapsibleNavTooltip collapsed={collapsed} content="Portfolio Analytics (Coming soon)">
            <span
              className={`w-full opacity-40 cursor-not-allowed ${itemClass}`}
              aria-disabled
            >
              <PieChart className={ICON_CLASS_SM} />
              {!collapsed && (
                <>
                  <span className="text-sm whitespace-nowrap">Portfolio Analytics</span>
                  <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0 h-4 bg-white/15 text-sidebar-foreground border-0">Soon</Badge>
                </>
              )}
            </span>
          </CollapsibleNavTooltip>

          {/*
            AUT-477: Keyword Trends sits here, not in the nav above, because it is global rather
            than brand-scoped — the design's own reason ("Keyword Trends — global, not
            brand-scoped", signal-v3 `AppSidebar.tsx`) — and the footer is where the other global
            items live. Between Portfolio Analytics and Notifications, as the design has it.
          */}
          <CollapsibleNavTooltip collapsed={collapsed} content="Keyword Trends">
            <NavLink
              to={RoutePath.KeywordTrends}
              className={`w-full ${itemClass}`}
              activeClassName="bg-black/15 text-sidebar-foreground font-normal"
            >
              <LineChart className={ICON_CLASS_SM} />
              {!collapsed && <span className="text-sm whitespace-nowrap">Keyword Trends</span>}
            </NavLink>
          </CollapsibleNavTooltip>

          {/* Notifications */}
          <CollapsibleNavTooltip collapsed={collapsed} content="Notifications">
            <button
              onClick={() => setNotificationsOpen(true)}
              className={`w-full ${itemClass}`}
            >
              <div className="relative shrink-0">
                <Bell className={ICON_CLASS_SM} />
                {unreadCount > 0 && <span className="absolute -top-1 -right-1 h-2 w-2 bg-primary rounded-full" />}
              </div>
              {!collapsed && <span className="text-sm whitespace-nowrap">Notifications</span>}
            </button>
          </CollapsibleNavTooltip>

          {/* Add Brand */}
          {canMutateBrands && !readOnly ? (
            <CollapsibleNavTooltip collapsed={collapsed} content="Add a Brand">
              <button
                onClick={() => setAddBrandModalOpen(true)}
                className={`w-full ${itemClass}`}
              >
                <Plus className={ICON_CLASS_SM} />
                {!collapsed && <span className="text-sm whitespace-nowrap">Add a Brand</span>}
              </button>
            </CollapsibleNavTooltip>
          ) : null}

          {/* Admin Panel */}
          {canManageWorkspace && (
            <CollapsibleNavTooltip collapsed={collapsed} content="Admin Panel">
              {/* AUT-399: a real link, so cmd/middle-click and "Open in new tab" work. It points at
                  an in-app staging post rather than the admin panel itself, because the SSO link is
                  single-use and expires in two minutes -- see `pages/AdminPanelRedirect.tsx`.
                  `target="_blank"` keeps the plain-click behaviour users have today. */}
              <Link
                to={RoutePath.AdminPanel}
                target="_blank"
                rel="noopener noreferrer"
                className={`w-full ${itemClass}`}
              >
                <Shield className={ICON_CLASS_SM} />
                {!collapsed && <span className="text-sm whitespace-nowrap">Admin Panel</span>}
              </Link>
            </CollapsibleNavTooltip>
          )}

          {/* User Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className={`w-full ${itemClass}`}>
                <User className={ICON_CLASS_SM} />
                {!collapsed && (
                  <>
                    <TruncatedText className="text-sm font-normal">{account?.email ?? ""}</TruncatedText>
                    <ChevronRight className="h-4 w-4 opacity-60 ml-auto" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="right" className="w-56 bg-popover border border-border">
              {/* AUT-399: `asChild` + `Link` so the item is a real anchor -- cmd/middle-click and
                  "Open in new tab" work, while a plain click still routes in-app. */}
              <DropdownMenuItem asChild className="cursor-pointer">
                <Link to={RoutePath.Profile}>
                  <User className="mr-2 h-4 w-4" />
                  Profile Settings
                </Link>
              </DropdownMenuItem>
              {canManageWorkspace ? (
                <DropdownMenuItem asChild className="cursor-pointer">
                  <Link to={RoutePath.Settings}>
                    <Settings className="mr-2 h-4 w-4" />
                    Global Settings
                    <Badge variant="secondary" className="ml-auto text-[10px] px-1.5 py-0 h-4 bg-primary-tint-soft text-primary border-0">Admin</Badge>
                  </Link>
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer" onClick={() => setHelpOpen(true)}>
                <HelpCircle className="mr-2 h-4 w-4" />
                Help & Support
              </DropdownMenuItem>
              <DropdownMenuItem className="cursor-pointer" onClick={() => setDocsOpen(true)}>
                <FileText className="mr-2 h-4 w-4" />
                Documentation
              </DropdownMenuItem>
              <DropdownMenuItem asChild className="cursor-pointer">
                <Link to={RoutePath.SignalGuide}>
                  <FileText className="mr-2 h-4 w-4" />
                  Signal Guide
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="cursor-pointer text-destructive" onClick={() => signOut()}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Modals */}
      {canMutateBrands && !readOnly ? (
        <AddBrandModal open={addBrandModalOpen} onOpenChange={setAddBrandModalOpen} />
      ) : null}
      <NotificationsModal open={notificationsOpen} onOpenChange={setNotificationsOpen} />
      <HelpSupportModal open={helpOpen} onOpenChange={setHelpOpen} />
      <DocumentationModal open={docsOpen} onOpenChange={setDocsOpen} />
    </>
  );
}
